import React, { useState } from 'react';
import { Layout, Menu, Button, Dropdown, Space, Avatar, Drawer } from 'antd';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  UserOutlined,
  TeamOutlined,
  FileTextOutlined,
  ProjectOutlined,
  LogoutOutlined,
  AuditOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import { authService } from '../services/authService';
import useResponsive from '../hooks/useResponsive';

const { Header, Sider, Content } = Layout;

const MainLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = authService.getUser();
  const currentUser = authService.getCurrentUser();
  const { isMobile } = useResponsive();
  const [drawerVisible, setDrawerVisible] = useState(false);

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
    if (isMobile) {
      setDrawerVisible(false); // 移动端点击菜单后关闭抽屉
    }
  };

  const userMenu = (
    <Menu>
      <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={handleLogout}>
        退出登录
      </Menu.Item>
    </Menu>
  );

  // 根据用户角色生成菜单项
  const getMenuItems = () => {
    const baseItems = [
      {
        key: '/',
        icon: <DashboardOutlined />,
        label: '首页',
      }
    ];

    const projectItems = [{ key: '/project-progress', icon: <ProjectOutlined />, label: '项目进度' }];
    if (currentUser?.role === 'readonly') return [{ key: '/project-view', icon: <ProjectOutlined />, label: '项目资料' }, ...projectItems];
    if (currentUser?.role === 'super_admin') {
      // super_admin 可以看到所有菜单
      return [
        ...baseItems,
        ...projectItems,
        {
          key: '/users',
          icon: <UserOutlined />,
          label: '用户管理',
        },
        {
          key: '/survey-subjects/doctors',
          icon: <TeamOutlined />,
          label: '医生管理',
        },
        {
          key: '/projects/survey',
          icon: <ProjectOutlined />,
          label: '项目管理',
        },
        {
          key: '/labor-management',
          icon: <AuditOutlined />,
          label: '劳务管理',
          children: [
            {
              key: '/labor-management/signatures',
              label: '劳务签署管理',
            },
            {
              key: '/project-labor-detail-index',
              label: '劳务明细管理',
            },
          ],
        },
        {
          key: '/questionnaires',
          icon: <FileTextOutlined />,
          label: '问卷管理',
        },
        {
          key: '/questionnaire-approval',
          icon: <FileTextOutlined />,
          label: '问卷审批',
        },
        {
          key: '/approver-management',
          icon: <AuditOutlined />,
          label: '审批员管理',
        },
      ];
    } else if (currentUser?.role === 'project_admin') {
      // project_admin 只能看到项目相关的菜单
      return [
        ...baseItems,
        ...projectItems,
        { key: '/users', icon: <UserOutlined />, label: '用户管理' },
        {
          key: '/survey-subjects/doctors',
          icon: <TeamOutlined />,
          label: '医生管理',
        },
        {
          key: '/projects/survey',
          icon: <ProjectOutlined />,
          label: '项目管理',
        },
        {
          key: '/labor-management',
          icon: <AuditOutlined />,
          label: '劳务管理',
          children: [
            {
              key: '/labor-management/signatures',
              label: '劳务签署管理',
            },
            {
              key: '/project-labor-detail-index',
              label: '劳务明细管理',
            },
          ],
        },
        {
          key: '/questionnaires',
          icon: <FileTextOutlined />,
          label: '问卷管理',
        },
        {
          key: '/questionnaire-approval',
          icon: <FileTextOutlined />,
          label: '问卷审批',
        },
      ];
    } else if (currentUser?.role === 'audit_admin') {
      // 审批员只需要问卷审批入口，可见范围由后端按其负责的项目过滤
      return [
        { key: '/project-view', icon: <ProjectOutlined />, label: '项目资料' },
        ...projectItems,
        {
          key: '/questionnaire-approval',
          icon: <FileTextOutlined />,
          label: '问卷审批',
        },
      ];
    }

    return baseItems;
  };

  const menuItems = getMenuItems();

  // 菜单内容组件（PC和移动端复用）
  const menuContent = (
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={[location.pathname]}
      defaultOpenKeys={['/survey-subjects', '/projects', '/labor-management']}
      items={menuItems}
      onClick={handleMenuClick}
    />
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* PC端侧边栏 */}
      {!isMobile && (
        <Sider width={200} theme="dark">
          <div style={{ height: 32, margin: 16, background: 'rgba(255, 255, 255, 0.2)' }} />
          {menuContent}
        </Sider>
      )}

      {/* 移动端抽屉 */}
      {isMobile && (
        <Drawer
          title="菜单"
          placement="left"
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          bodyStyle={{ padding: 0, background: '#001529' }}
          headerStyle={{ background: '#001529', color: '#fff' }}
        >
          {menuContent}
        </Drawer>
      )}

      <Layout>
        <Header style={{ 
          background: '#fff', 
          padding: isMobile ? '0 16px' : '0 24px',
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 1px 4px rgba(0,21,41,.08)'
        }}>
          {/* 移动端菜单按钮 */}
          {isMobile && (
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setDrawerVisible(true)}
              style={{ fontSize: '18px' }}
            />
          )}
          
          <div style={{ marginLeft: 'auto' }}>
            <Dropdown overlay={userMenu} placement="bottomRight">
              <Space style={{ cursor: 'pointer' }}>
                <Avatar icon={<UserOutlined />} />
                {!isMobile && <span>{user?.username || '用户'}</span>}
              </Space>
            </Dropdown>
          </div>
        </Header>
        <Content style={{ 
          margin: isMobile ? '16px 8px' : '24px 16px', 
          padding: isMobile ? 16 : 24, 
          background: '#fff' 
        }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;